import type {
  CreateImageGenerationTaskPayload,
  ImageGenerationConfig,
  ImageGenerationProviderChannel,
  ImageGenerationRoutePolicy,
  ImageGenerationTask,
} from '../../../api'

import { filterChannelsByAuth } from './authMatrixRuntime'
import { FREE_RATIO, ratioForSubmit } from './imageGenerationRatio'

/** Canvas image generation provider/channel selection (product layer). */

export const PROVIDER_CHANNEL_BFL = 'CLOUD_FLUX2_PRO'
export const PROVIDER_CHANNEL_STANDARD = 'STANDARD_CHATGPT'
export const PROVIDER_CHANNEL_LOCAL = 'LOCAL_FLUX'
export const PROVIDER_CHANNEL_LEGACY = 'LEGACY'

export type { ImageGenerationConfig, ImageGenerationProviderChannel, ImageGenerationRoutePolicy }

export interface ImageGenerationProviderConfigShape {
  provider_channels?: ImageGenerationProviderChannel[]
  default_provider_option_id?: string | null
  provider_option_channel_aliases?: Record<string, string> | null
}

export function parseProviderChannels(
  config: ImageGenerationProviderConfigShape | ImageGenerationConfig | null | undefined,
): ImageGenerationProviderChannel[] {
  return Array.isArray(config?.provider_channels) ? config.provider_channels : []
}

/** Tenant-facing provider dropdown — backend filters too (TASK-00310). */
export function tenantSelectableImageProviderChannels(
  channels: ImageGenerationProviderChannel[],
): ImageGenerationProviderChannel[] {
  return channels.filter((channel) => isImageProviderExecutionSupported(channel))
}

/** VistoeraTASK-00634 — hide channels the current member cannot use for generate_image. */
export function authFilteredImageProviderChannels(
  channels: ImageGenerationProviderChannel[],
  nodeType = 'generate_image',
): ImageGenerationProviderChannel[] {
  return filterChannelsByAuth(nodeType, tenantSelectableImageProviderChannels(channels))
}

function resolvePersistedProviderChannelId(
  fields: Record<string, unknown>,
  config: ImageGenerationProviderConfigShape | ImageGenerationConfig | null | undefined,
): string | undefined {
  const raw = fields.providerChannel as string | undefined
  if (!raw || raw === PROVIDER_CHANNEL_LEGACY) return undefined
  if (raw.startsWith('PROVIDER_OPTION::')) {
    const optionId = raw.slice('PROVIDER_OPTION::'.length)
    const alias = config?.provider_option_channel_aliases?.[optionId]
    if (alias) return alias
  }
  return raw
}

export function resolveProviderChannel(
  fields: Record<string, unknown>,
  channels: ImageGenerationProviderChannel[],
  config?: ImageGenerationProviderConfigShape | ImageGenerationConfig | null,
): ImageGenerationProviderChannel | null {
  const resolvedId = resolvePersistedProviderChannelId(fields, config)
  if (!resolvedId) return null
  return channels.find((c) => c.id === resolvedId) ?? null
}

/** Resolve Admin canonical default → Canvas channel id (VistoeraTASK-00306). */
export function resolveDefaultProviderChannelId(
  config: ImageGenerationProviderConfigShape | ImageGenerationConfig | null | undefined,
  channels: ImageGenerationProviderChannel[],
): string | null {
  const defaultProviderOptionId = config?.default_provider_option_id?.trim()
  if (!defaultProviderOptionId) return null
  const match = channels.find((c) => c.provider_option_id === defaultProviderOptionId)
  return match?.id ?? null
}

const PROVIDER_OPTION_PREFIX = 'PROVIDER_OPTION::'

function resolveRoutePolicyModelId(
  policy: ImageGenerationRoutePolicy | null | undefined,
  fallbackModelId?: string,
): string | undefined {
  const fromPolicy = policy?.model_id || policy?.model_preference
  return fromPolicy || fallbackModelId || undefined
}

/** Submit payload uses canonical fields only — never duplicate model identity aliases. */
function buildSubmitRoutePolicy(
  policy: ImageGenerationRoutePolicy,
  fallbackModelId?: string,
): ImageGenerationRoutePolicy {
  const modelId = resolveRoutePolicyModelId(policy, fallbackModelId)
  return {
    provider_preference: policy.provider_preference ?? undefined,
    route_mode: policy.route_mode ?? undefined,
    model_id: modelId,
    provider_option_id: policy.provider_option_id ?? undefined,
    cloud_fallback_allowed: policy.cloud_fallback_allowed ?? false,
  }
}

/** Resolve the channel used for submit — live config first, then persisted snapshot. */
export function resolveSubmitProviderChannel(
  fields: Record<string, unknown>,
  channels: ImageGenerationProviderChannel[],
  config?: ImageGenerationProviderConfigShape | ImageGenerationConfig | null,
): ImageGenerationProviderChannel | null {
  const live = resolveProviderChannel(fields, channels, config)
  if (live) return live

  const raw = fields.providerChannel as string | undefined
  if (!raw || raw === PROVIDER_CHANNEL_LEGACY) return null

  const snapshot = fields.providerRoutePolicy as ImageGenerationRoutePolicy | null | undefined
  if (snapshot && typeof snapshot === 'object') {
    const modelId = resolveRoutePolicyModelId(snapshot, fields.modelId as string | undefined)
    return {
      id: raw,
      label: raw,
      billing_hint: '',
      generation_mode: 'FAST',
      route_policy: {
        ...snapshot,
        model_id: modelId,
      },
      supports_multi_reference: true,
      max_reference_images: 1,
      provider_max_reference_images: 1,
      model_id: modelId || raw,
      provider_id: (fields.providerId as string | undefined) || snapshot.provider_preference || raw,
    }
  }

  if (!raw.startsWith(PROVIDER_OPTION_PREFIX)) return null
  const providerOptionId = raw.slice(PROVIDER_OPTION_PREFIX.length)
  const modelId = fields.modelId as string | undefined
  return {
    id: raw,
    label: raw,
    billing_hint: '',
    generation_mode: 'FAST',
    route_policy: {
      route_mode: 'NODE_MODEL_BINDING',
      provider_preference: (fields.providerId as string | undefined) || undefined,
      provider_option_id: providerOptionId,
      model_id: modelId,
      cloud_fallback_allowed: false,
    },
    supports_multi_reference: true,
    max_reference_images: 1,
    provider_max_reference_images: 1,
    model_id: modelId || providerOptionId,
    provider_id: (fields.providerId as string | undefined) || providerOptionId,
    provider_option_id: providerOptionId,
  }
}

export function hasUnresolvedExplicitProviderChannel(
  fields: Record<string, unknown>,
  channels: ImageGenerationProviderChannel[],
): boolean {
  const raw = fields.providerChannel as string | undefined
  if (!raw || raw === PROVIDER_CHANNEL_LEGACY) return false
  if (channels.length === 0) return false
  return resolveSubmitProviderChannel(fields, channels) == null
}

/** Backend-owned readiness — never re-derive executability client-side. */
export function isImageProviderExecutionSupported(
  channel: ImageGenerationProviderChannel | null | undefined,
): boolean {
  if (!channel) return true
  const flag = (channel.readiness as { execution_supported?: unknown } | undefined)?.execution_supported
  return flag !== false
}

type ImageProviderReadiness = {
  execution_supported?: boolean
  unavailable_reason?: string | null
}

/** Tenant-facing block message — prefer backend ``unavailable_reason`` over preview billing copy. */
export function imageProviderExecutionBlockMessage(
  channel: ImageGenerationProviderChannel | null | undefined,
  fallbackMessage: string,
): string {
  if (!channel) return fallbackMessage
  const readiness = channel.readiness as ImageProviderReadiness | undefined
  const reason = readiness?.unavailable_reason?.trim()
  if (reason) return reason
  const tooltip = (channel.billing_tooltip as string | undefined)?.trim()
  if (tooltip) return tooltip
  return fallbackMessage
}

export function channelSupportsReferenceCount(
  channel: ImageGenerationProviderChannel,
  referenceCount: number,
): boolean {
  const max = channel.max_reference_images
  if (typeof max === 'number' && Number.isFinite(max)) {
    return referenceCount <= max
  }
  if (referenceCount <= 0) return true
  return Boolean(channel.supports_multi_reference)
}

/**
 * Pick the first execution-ready channel that satisfies reference input
 * (admin sort order). Used by routing tests and future provider-auto UX;
 * never silently applied on manual provider selection (VistoeraTASK-00305).
 */
export function pickCompatibleExecutionReadyImageChannel(
  channels: ImageGenerationProviderChannel[],
  referenceCount: number,
): ImageGenerationProviderChannel | null {
  for (const channel of channels) {
    if (!isImageProviderExecutionSupported(channel)) continue
    if (!channelSupportsReferenceCount(channel, referenceCount)) continue
    return channel
  }
  return null
}

/**
 * VistoeraTASK-00253 canonical §三 default-selection rule for a
 * brand-new ``generate_image`` node — never a hardcoded Provider
 * literal (task background's explicit hard rule: this function used to
 * unconditionally set ``providerChannel: PROVIDER_CHANNEL_BFL`` for
 * every Preview-environment workspace, completely ignoring Platform
 * Admin's real, already-correct ``is_default`` flag — confirmed live
 * on Preview: the backend already returns ``is_default: true`` on the
 * "标准 · ChatGPT" channel, this function just never looked at it).
 *
 * Exactly three tiers, in order, never a fourth silently-guessed one:
 * 1) The channel Platform Admin currently marks as default
 *    (`channel.is_default`).
 * 2) No channel is currently marked default (task spec: "如果默认方案
 *    被禁用/删除/租户不可见") -> the first channel in the list, which is
 *    already the backend's own canonical admin-sort_order order
 *    (`image_generation_provider_config.build_provider_channels_
 *    config`'s own final sort) — never a frontend-hardcoded Provider,
 *    never `providers[0]` picked before real sorting, never re-sorted
 *    here by provider/model name.
 * 3) `channels` is empty (config not loaded yet, or genuinely zero
 *    legal execution plans for this tenant) -> return `base` with NO
 *    `providerChannel`/`modelId` key at all, so Canvas's own Select
 *    renders its existing "unavailable/未选择" placeholder state
 *    instead of silently defaulting to any Provider.
 *
 * Runs in EVERY environment now (the old `isPreviewCanvasEnvironment`
 * gate is gone) — the previous Preview-only special case was itself
 * part of the bug: outside Preview a brand-new node got NO default at
 * all, which is no more correct than Preview's wrong hardcoded one.
 */
export function buildGenerateImageNodeDefaultFields(
  workspaceId: string,
  config?: ImageGenerationProviderConfigShape | ImageGenerationConfig | null,
): Record<string, unknown> {
  void workspaceId // kept for call-site stability / future tenant-scoped override (see ProviderOptionScopeType.TENANT prep) — not consulted this round, V1 is PLATFORM-scope only.
  const base = {
    prompt: '',
    // VistoeraTASK-00498 — new nodes default to "自由比例（由提示词决定）".
    ratio: FREE_RATIO as string,
    quality: 'STANDARD',
    candidateCount: 1,
    strength: 0.75,
    status: 'READY',
  }
  const channels = parseProviderChannels(config)
  if (channels.length === 0) return base
  const defaultChannelId = resolveDefaultProviderChannelId(config, channels)
  const defaultChannel =
    (defaultChannelId ? channels.find((c) => c.id === defaultChannelId) : null) ??
    channels.find((c) => c.is_default) ??
    null
  if (!defaultChannel) return base
  return {
    ...base,
    providerChannel: defaultChannel.id,
    modelId: defaultChannel.model_id,
  }
}

export function buildImageGenerationSubmitPayload(args: {
  channel: ImageGenerationProviderChannel | null
  prompt: string
  operation: CreateImageGenerationTaskPayload['operation']
  /** Omit / `undefined` / free sentinel => NO aspect-ratio constraint (VistoeraTASK-00498). */
  ratio?: string | null
  quality: string
  candidateCount: number
  strength?: number
  referenceImageInputs?: Array<{
    asset_id: string
    asset_ref: string
    label: string
    index: number
    role_optional?: string | null
  }>
  promptAssetBindings?: Array<{ token: string; asset_ref: string }>
  sourceContext?: Record<string, unknown>
}): CreateImageGenerationTaskPayload {
  const payload: CreateImageGenerationTaskPayload = {
    prompt: args.prompt,
    operation: args.operation,
    quality: args.quality,
    candidate_count: args.candidateCount,
    source_context: args.sourceContext,
  }
  // Free ratio => the `ratio` key is OMITTED (never "free"/"auto"/null/"1:1").
  const submitRatio = ratioForSubmit(args.ratio)
  if (submitRatio !== undefined) payload.ratio = submitRatio
  if (args.strength != null) payload.strength = args.strength
  if (args.referenceImageInputs?.length) {
    payload.reference_image_inputs = args.referenceImageInputs
    payload.references = args.referenceImageInputs.map((r) => ({
      asset_id: r.asset_id,
      role: r.role_optional || 'reference_image',
    }))
  }
  if (args.promptAssetBindings?.length) {
    payload.prompt_asset_bindings = args.promptAssetBindings
  }
  if (!args.channel) return payload
  payload.generation_mode = args.channel.generation_mode
  if (args.channel.route_policy) {
    payload.route_policy = buildSubmitRoutePolicy(args.channel.route_policy, args.channel.model_id)
  }
  return payload
}

/** Local FLUX Engine Lifecycle pending UI (spec §13/§31/§53) — mirrors
 * `videoGenerationProvider.ts`'s `resolveCanvasVideoTaskFieldStatus`
 * exactly: while the Job is queued and waiting on Residency Manager
 * activation, the Canvas field status must render a distinct
 * "正在启动本地模型…" state, never the generic "等待中…"/网络失败 wording. */
export function resolveCanvasImageTaskFieldStatus(task: ImageGenerationTask): string {
  if (task.waiting_for_engine || task.engine_activation?.waiting_for_engine) {
    return 'WAITING_FOR_ENGINE'
  }
  if (task.status === 'succeeded') return 'COMPLETED'
  if (task.status === 'failed') return 'FAILED'
  // VistoeraTASK-00148 (§四/§十一) — INTERRUPTED (Worker Lease/heartbeat
  // genuinely lost, or a Control-Plane-restart orphan recovery) is a
  // DISTINCT terminal-ish outcome from an in-flight RUNNING generation —
  // it must render its own "任务中断，可重新生成" state, never the same
  // "GENERATING" spinner a live task shows. Previously conflated with
  // 'running' here, which hid a genuinely stuck/lost task behind an
  // infinite-looking spinner instead of prompting the user to
  // regenerate (a new task_id, per §十三 — never a resurrection of this
  // one).
  if (task.status === 'interrupted') return 'INTERRUPTED'
  if (task.status === 'running') {
    const stage = task.execution?.stage
    if (stage === 'uploading_assets') return 'UPLOADING_ASSETS'
    if (stage === 'submitting') return 'SUBMITTING_REQUEST'
    if (stage === 'retrying') return 'RETRYING'
    if (stage === 'queued' || stage === 'claiming') return 'QUEUED'
    return 'GENERATING'
  }
  return 'QUEUED'
}
